import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ContainerLayout } from 'layouts';
import { FontBodyL, DigitsInput } from 'components/shared';
import replaceStarsWithBulletPoints from 'utils/replaceStarsWithBulletPoints/replaceStarsWithBulletPoints';
import { TranslationContext } from '@ds/react-utils';
import { useButtonsLoading, useInterval } from 'hooks';
import { apiService } from 'services';
import { routes } from 'static/routes';
import { setFocus } from 'utils/AccessibilityUtils';
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import ResendModal from 'components/shared/ResendModal/ResendModal';
import { IPhoneNumber } from 'services/PhoneNumber';
import { sendCall, sendSMS } from 'queries/PhoneQueries';
import { solveEmail, sendEmail, IEmailAddressResponse, IVerificationResponse } from 'queries/EmailQueries';
import { ContinueModel } from 'types/ContinueModel';
import { PhoneVerificationResponseMessages } from 'types/PhoneVerificationResponseMessages';
import { AxiosError } from 'axios';

type PhoneLocationState = {
    selectedPhoneNumber: IPhoneNumber;
    method: string;
};

type EmailLocationState = {
    emailAddress: IEmailAddressResponse;
};

type LocationState = PhoneLocationState | EmailLocationState;

const ConfirmCodePage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const { state } = useLocation<LocationState>();
    const history = useHistory();
    const translate = useContext(TranslationContext);

    const isEmail = 'emailAddress' in state;

    const [code, setCode] = useState('');
    const [textBoxError, setTextBoxError] = useState('');
    const formRef = useRef<HTMLFormElement>(null);
    const [continueModel, setContinueModel] = useState<ContinueModel | undefined>(undefined);

    const countdownDuration = 5;
    const [countdown, setCountdown] = useState(countdownDuration);
    useInterval(() => {
        if (countdown > 0) {
            setCountdown(countdown - 1);
        }
    }, 1000);

    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading, isSecondaryButtonLoading, areButtonsLoading } = useButtonsLoading();

    const resetCountdown = () => {
        setCountdown(countdownDuration);
    };

    const setFocusOnCodeInput = () => {
        setFocus('[data-focus="verify-confirm-code-verification-code-input"]');
    };

    useEffect(() => {
        setFocusOnCodeInput();
    }, []);

    useEffect(() => {
        if (
            !!continueModel &&
            !!continueModel.callbackUri &&
            !!continueModel.claimResponse &&
            !!formRef.current?.action
        ) {
            formRef.current.submit();
        }
    }, [continueModel]);

    const onErrorCallback = (error: AxiosError) => {
        if (error?.response?.status === 401) {
            history.push(routes.SessionExpired);
        } else {
            history.push(routes.Error);
        }
    };

    const getContinueModel = () => {
        apiService.get<ContinueModel>(
            '/App/GetClaimResponse',
            (response: ContinueModel) => {
                setContinueModel(response);
            },
            (error: AxiosError) => {
                if (error?.response?.status === 401) {
                    history.push(routes.SessionExpired);
                    return;
                }
                history.push(isEmail ? routes.Error : routes.ChooseMethod);
            }
        );
    };

    const handleResponse = (response: IVerificationResponse | PhoneVerificationResponseMessages) => {
        if (response.responseResult === 'Success') {
            getContinueModel();
        } else if (response.responseResult === 'Error') {
            if (response.responseMessage === 'VerificationCodeMisMatchError') {
                setTextBoxError(translate('Code is incorrect'));
                setFocusOnCodeInput();
                setIsPrimaryButtonLoading(false);
            } else if (response.responseMessage === 'MaxAttemptsExceeded') {
                history.push(routes.DocumentLocked);
            } else {
                setIsPrimaryButtonLoading(false);
                history.push(routes.Error);
            }
        }
    };

    const updateInput = (codes: string) => {
        setTextBoxError('');
        setCode(codes);
    };

    const onKeyDown = (key: string) => {
        if (key === 'Enter') {
            onConfirmCodeClick();
        }
    };

    const onConfirmCodeClick = useCallback(async () => {
        if (areButtonsLoading) {
            return;
        }

        if (code.length !== 6 || code.match(/[0-9]{6}/)?.length !== 1) {
            setTextBoxError(translate('Code must be 6 numbers'));
            setFocusOnCodeInput();
            return;
        }

        setIsPrimaryButtonLoading(true);

        if (isEmail) {
            solveEmail(
                { code },
                (response: IVerificationResponse) => handleResponse(response),
                onErrorCallback
            );
        } else {
            apiService.post<any>(
                'phone/solve',
                { code },
                (response: PhoneVerificationResponseMessages) => handleResponse(response),
                onErrorCallback
            );
        }
    }, [areButtonsLoading, code, setIsPrimaryButtonLoading, translate, isEmail]);

    const maskedValue = isEmail
        ? replaceStarsWithBulletPoints((state as EmailLocationState).emailAddress?.maskedEmail ?? '')
        : replaceStarsWithBulletPoints((state as PhoneLocationState).selectedPhoneNumber?.phoneNumber ?? '');

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title title={translate('Enter your code')} titleDataQa="verify-confirm-code-title" />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <FontBodyL data-qa="verify-confirm-code-intro">{translate('We sent a code to')}</FontBodyL>
                <FontBodyL data-qa="verify-confirm-code-phone-number">
                    <strong>{maskedValue}</strong>
                </FontBodyL>

                <DigitsInput
                    onChange={updateInput}
                    onKeyDown={onKeyDown}
                    label={translate('Verification Code')}
                    labelId="verify-confirm-code-verification-code-label"
                    numberOfDigits={6}
                    dataQa="verify-confirm-code-verification-code-input"
                    dataFocus="verify-confirm-code-verification-code-input"
                    autocomplete="one-time-code"
                    error={translate(textBoxError)}
                />

                <ResendModal
                    countdown={countdown}
                    resetCountdown={resetCountdown}
                    method={isEmail ? 'Email' : (state as PhoneLocationState).method}
                    displayChannel={context.displayChannel || ''}
                    onNewSms={() => {
                        if (!isEmail) {
                            const { selectedPhoneNumber } = state as PhoneLocationState;
                            sendSMS(
                                {
                                    phoneNumber: selectedPhoneNumber.phoneNumber,
                                    uniqueId: selectedPhoneNumber.uniqueId,
                                    language: context.language
                                },
                                () => {},
                                onErrorCallback
                            );
                        }
                    }}
                    onNewCall={() => {
                        if (!isEmail) {
                            const { selectedPhoneNumber } = state as PhoneLocationState;
                            sendCall(
                                {
                                    phoneNumber: selectedPhoneNumber.phoneNumber,
                                    uniqueId: selectedPhoneNumber.uniqueId,
                                    language: context.language
                                },
                                () => {},
                                onErrorCallback
                            );
                        }
                    }}
                    onNewEmail={() => {
                        if (isEmail) {
                            const { emailAddress } = state as EmailLocationState;
                            sendEmail(
                                {
                                    uniqueId: emailAddress.uniqueId,
                                    language: context.language
                                },
                                () => {},
                                onErrorCallback
                            );
                        }
                    }}
                />
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Buttons>
                    <ContainerLayout.Footer.Buttons.Secondary
                        data-qa="verify-confirm-code-secondary-button"
                        text={translate('Back')}
                        onClick={history.goBack}
                        loading={isSecondaryButtonLoading}
                    />
                    <ContainerLayout.Footer.Buttons.Primary
                        data-qa="verify-confirm-code-primary-button"
                        text={translate('Confirm Code')}
                        onClick={onConfirmCodeClick}
                        loading={isPrimaryButtonLoading}
                    />
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
            {/* FORM THAT WILL MAKE A POST BACK TO ACCOUNT SERVER AFTER THE CODE IS VERIFIED */}
            <form ref={formRef} method="POST" action={continueModel ? continueModel.callbackUri : ''} data-qa="verify-confirm-code-form">
                <input
                    type="hidden"
                    name="claim_response"
                    value={continueModel ? continueModel.claimResponse : ''}
                    data-qa="verify-confirm-code-form-input"
                ></input>
            </form>
        </ContainerLayout>
    );
};

export default ConfirmCodePage;
